import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model } from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

export type BzSwitchSize = 'sm' | 'md';

/**
 * An on/off switch. Same three binding modes as `bz-checkbox`: `[(checked)]`,
 * `formControlName`, or `ngModel`.
 *
 * A switch and a checkbox are not interchangeable: a switch applies its
 * change immediately, a checkbox stages a value until something is
 * submitted. Pick accordingly rather than by which one looks better in a
 * given layout.
 */
@Component({
  selector: 'bz-switch',
  templateUrl: './bz-switch.html',
  styleUrl: './bz-switch.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BzSwitch),
      multi: true,
    },
  ],
})
export class BzSwitch implements ControlValueAccessor {
  readonly checked = model(false);

  /**
   * Accessible name for the control when nothing is projected as a visible
   * label. Without it a bare switch has no name at all and fails an
   * AXE check — there is no text for a screen reader to read.
   */
  readonly ariaLabel = input('');
  readonly disabled = model(false);
  readonly size = input<BzSwitchSize>('md');

  protected readonly wrapperClass = computed(() => `bz-switch bz-switch--${this.size()}`);

  private propagateChange: (value: boolean) => void = () => undefined;
  private propagateTouched: () => void = () => undefined;

  writeValue(value: boolean): void {
    this.checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected toggle(): void {
    if (this.disabled()) {
      return;
    }

    const next = !this.checked();
    this.checked.set(next);
    this.propagateChange(next);
  }

  protected markTouched(): void {
    this.propagateTouched();
  }
}
